import React, { useState } from 'react';
import {
  FileText, Mail, Share2, Hash, HelpCircle, Megaphone,
  Image, Video, FileIcon, ChevronRight, Sparkles
} from 'lucide-react';
import { assetTypes } from './utils/assetTypeConfig';

interface AssetTypeSelectorProps {
  onSelect: (typeId: string) => void;
  selectedType: string;
}

type CategoryTab = 'text' | 'media' | 'ads';

export default function AssetTypeSelector({ onSelect, selectedType }: AssetTypeSelectorProps) {
  const [activeTab, setActiveTab] = useState<CategoryTab>('text');

  // Debug logging
  console.log('=== AssetTypeSelector Loaded ===');
  console.log('Total asset types imported:', assetTypes.length);
  console.log('Asset types:', assetTypes.map(t => ({ id: t.id, label: t.label, category: t.category })));

  const getIcon = (iconName: string) => {
    const iconClass = "w-5 h-5";
    switch (iconName) {
      case 'FileText': return <FileText className={iconClass} />;
      case 'Mail': return <Mail className={iconClass} />;
      case 'Share2': return <Share2 className={iconClass} />;
      case 'Hash': return <Hash className={iconClass} />;
      case 'HelpCircle': return <HelpCircle className={iconClass} />;
      case 'Megaphone': return <Megaphone className={iconClass} />;
      case 'Image': return <Image className={iconClass} />;
      case 'Video': return <Video className={iconClass} />;
      default: return <FileIcon className={iconClass} />;
    }
  };

  const textAssets = assetTypes.filter(type => type.category === 'text');
  const mediaAssets = assetTypes.filter(type => type.category === 'media');
  const adAssets = assetTypes.filter(type => type.category === 'ads');

  console.log('Text Assets Count:', textAssets.length, textAssets.map(t => t.label));
  console.log('Media Assets Count:', mediaAssets.length, mediaAssets.map(t => t.label));
  console.log('Ad Assets Count:', adAssets.length, adAssets.map(t => t.label));

  const AssetTypeCard = ({ type }: { type: typeof assetTypes[0] }) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Button clicked for type:', type.id);
        onSelect(type.id);
      }}
      className={`w-full relative p-4 rounded-lg border-2 text-left cursor-pointer ${
        selectedType === type.id
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${
          selectedType === type.id
            ? 'bg-blue-100 text-blue-600'
            : 'bg-gray-100 text-gray-600'
        }`}>
          {getIcon(type.icon)}
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">{type.label}</h4>
          <p className="text-sm text-gray-600 mt-1">{type.description}</p>
          {!type.requiresUpload && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 mt-2">
              <Sparkles className="w-3 h-3" />
              No upload required
            </span>
          )}
        </div>
        <ChevronRight className={`w-5 h-5 ${
          selectedType === type.id ? 'text-blue-500' : 'text-gray-400'
        }`} />
      </div>
    </button>
  );

  // Get assets for active tab
  const getActiveAssets = () => {
    switch (activeTab) {
      case 'text':
        return textAssets;
      case 'media':
        return mediaAssets;
      case 'ads':
        return adAssets;
      default:
        return textAssets;
    }
  };

  const activeAssets = getActiveAssets();

  console.log('Active Tab:', activeTab);
  console.log('Active Assets Count:', activeAssets.length);
  console.log('Active Assets:', activeAssets.map(t => t.label));

  const tabs = [
    { id: 'text' as CategoryTab, label: '📝 Text & Content', count: textAssets.length },
    { id: 'media' as CategoryTab, label: '📸 Media', count: mediaAssets.length },
    { id: 'ads' as CategoryTab, label: '📢 Advertising', count: adAssets.length }
  ];

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Asset categories">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-3 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
              <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                activeTab === tab.id
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Description */}
      <div>
        <p className="text-gray-600 text-sm">
          {activeTab === 'text' && 'Create text-based assets directly in the browser without uploading files.'}
          {activeTab === 'media' && 'Upload images, videos, and documents to your content library.'}
          {activeTab === 'ads' && 'Configure advertising campaigns for Google, Facebook, LinkedIn, and more.'}
        </p>
      </div>

      {/* Asset Type Grid */}
      <div className="grid grid-cols-2 gap-3">
        {activeAssets.map(type => (
          <AssetTypeCard key={type.id} type={type} />
        ))}
      </div>

      {activeAssets.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>No asset types available in this category yet.</p>
        </div>
      )}
    </div>
  );
}